import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UsersTable = ({ users, onSort, sortField, sortDirection }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedUsers(users?.map(u => u?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const SortableHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-base"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortField === field && (
          <Icon 
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
            color="currentColor" 
          />
        )}
      </div>
    </th>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'inactive': return 'bg-muted text-muted-foreground';
      default: return 'bg-warning/10 text-warning';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-sm overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={selectedUsers?.length === users?.length}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                />
              </th>
              <SortableHeader field="name">Name</SortableHeader>
              <SortableHeader field="email">Email</SortableHeader>
              <SortableHeader field="registrationDate">Registration Date</SortableHeader>
              <SortableHeader field="analysisCount">Analyses</SortableHeader>
              <SortableHeader field="lastActive">Last Active</SortableHeader>
              <SortableHeader field="status">Status</SortableHeader>
              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {users?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/20 transition-base">
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => handleSelectUser(user?.id)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-foreground">{user?.name}</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-foreground">{user?.email}</td>
                <td className="px-4 py-4 text-sm text-foreground text-data">{user?.registrationDate}</td>
                <td className="px-4 py-4 text-sm text-foreground text-data text-center">{user?.analysisCount}</td>
                <td className="px-4 py-4 text-sm text-foreground text-data">{user?.lastActive}</td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                    {user?.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" iconName="Eye">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" iconName="MoreVertical" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-border">
        {users?.map((user) => (
          <div key={user?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <input
                  type="checkbox"
                  checked={selectedUsers?.includes(user?.id)}
                  onChange={() => handleSelectUser(user?.id)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary flex-shrink-0"
                />
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground truncate">{user?.name}</h4>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user?.status)} flex-shrink-0 ml-2`}>
                {user?.status}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground text-caption mb-1">Registered</p>
                <p className="text-foreground text-data">{user?.registrationDate}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground text-caption mb-1">Last Active</p>
                <p className="text-foreground text-data">{user?.lastActive}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground text-caption mb-1">Analyses</p>
                <p className="text-foreground text-data font-medium">{user?.analysisCount}</p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="Eye" className="flex-1">
                View Details
              </Button>
              <Button variant="ghost" size="sm" iconName="MoreVertical" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersTable;